import React, { useEffect, useRef, useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import "./StoryboardBoard.scss";
import VisualizationBoardContainer from "@viz-ui/components/StoryBoardsChart/VisualizationBoardContainer";
import DataGrid from "@viz-ui/components/dataGrid/dataGrid";
import ColumnConfig from "@viz-ui/models/tableConfig/columnConfig";
import Field from "@viz-ui/models/field/field";
import TileLoading from "@viz-ui/components/TileLoading/TileLoading";
import BoardNotification from "@viz-ui/components/common/boardNotification/boardNotification";
import TextBoard from "../../../../storyboards/modules/components/text_board.es6.jsx";
import MetricIndicator, { MetricHeader } from "@acl-services/sriracha-metrics/dist/Metrics";
import "@acl-services/sriracha-metrics/dist/Metrics.scss";
import StoryBoardDrilldownButton from "@viz-ui/components/drillDownButton/storyboardDrilldownButton";

if (typeof window !== "undefined") {
  window.MetricHeader = MetricHeader;
}

import services from "../../../../storyboards/services/glue/appGlue";
import { ERROR_CODES } from "../../../../storyboards/modules/boardDataLoader/errorCode.service";
import GlobalValueFormatter from "@viz-ui/services/formatters/globalValueFormatter";
import ConditionalFormatting from "@viz-ui/services/quickMenu/conditionalFormat/conditionalFormattingService";
import User from "../../../../storyboards/modules/services/user.service";
import LocalStorageHelper from "@viz-ui/services/localStorage/localStorageHelper";
import UrlFlipper from "@viz-ui/services/urlFlipper/urlFlipper";
import BoardOrganizer from "../../../../storyboards/modules/boardOrganizer/boardOrganizer.service";
import tableCapability from "@visualizer/modules/core/tableCapability/tableCapability.service";

const extractServicesWithFallbacks = () => {
  try {
    const {
      AppConfig: serviceAppConfig = { features: {} },
      Localize,
      UnsavedData,
      ColorPalette,
      DataFilter,
      AccessChecker,
    } = services || {};

    return {
      AppConfig: serviceAppConfig ||
        (typeof window !== "undefined" && window.AppConfig) || {
          features: {
            drillDownButton: true,
            enableStoryboardReactTextBoards: true,
          },
        },
      Localize,
      UnsavedData,
      ColorPalette,
      DataFilter,
      AccessChecker,
    };
  } catch (error) {
    return {
      AppConfig: {
        features: {
          drillDownButton: true,
          enableStoryboardReactTextBoards: true,
        },
      },
      Localize: null,
      UnsavedData: null,
      ColorPalette: null,
      DataFilter: null,
      AccessChecker: null,
    };
  }
};

const { AppConfig, Localize, UnsavedData, ColorPalette, DataFilter, AccessChecker } = extractServicesWithFallbacks();

const StoryboardBoard = ({
  board,
  editMode,
  fontZoomRatio,
  globalFilters,
  storyboardModel,
  boardModel,
  onRemoveBoard,
  onLoadMoreData,
  presentationMode,
  onDrilldownNoDataConfirmation,
}) => {
  const [hasNoData, setHasNoData] = useState(false);
  const [error, setError] = useState(null);
  const [showBoard, setShowBoard] = useState(false);
  const [isDrilldown, setIsDrilldown] = useState(false);
  const [metricBoard, setMetricBoard] = useState({});
  const [metricLoaded, setMetricLoadedOriginal] = useState(false);
  const [tableBoardLoaded, setTableBoardLoaded] = useState(false);

  const setMetricLoaded = useCallback(
    value => {
      setMetricLoadedOriginal(value);
    },
    [presentationMode, board.type, board.id, setMetricLoadedOriginal]
  );
  const [processedBoardModelId, setProcessedBoardModelId] = useState(null);

  const [isPatternFill, setIsPatternFill] = useState(() => {
    return UrlFlipper.isEnabled("isPatternFill") || LocalStorageHelper.get("applyPatternFill") || false;
  });

  const [calculatedTableHeight, setCalculatedTableHeight] = useState(null);
  const [dataVersion, setDataVersion] = useState(0);

  const elementRef = useRef(null);
  const redrawTimeoutRef = useRef(null);
  const metricIndicatorRef = useRef(null);
  const tableContainerRef = useRef(null);
  const previousDataSignature = useRef(null);

  // Constants from original Angular component
  const ACCESS_CHECKER_ENTITIES_MAP = {
    visualization: "interpretation",
    table: "interpretation",
    metric: "metric",
    trigger: "metric",
  };

  const showLoadingSpinner = AppConfig && AppConfig.features && AppConfig.features.showLoadingSpinner;

  useEffect(() => {
    const handlePatternFillChange = () => {
      const newPatternFillValue =
        UrlFlipper.isEnabled("isPatternFill") || LocalStorageHelper.get("applyPatternFill") || false;
      setIsPatternFill(newPatternFillValue);
    };

    window.addEventListener("storage", handlePatternFillChange);
    window.addEventListener("patternFillChanged", handlePatternFillChange);
    return () => {
      window.removeEventListener("storage", handlePatternFillChange);
      window.removeEventListener("patternFillChanged", handlePatternFillChange);
    };
  }, []);

  useEffect(() => {
    if (boardModel && showBoard) {
      const timeout = setTimeout(() => {
        const event = new CustomEvent("chartRedraw");
        window.dispatchEvent(event);
        if (window.angular && window.angular.element) {
          const scope = window.angular.element(document.body).scope();
          if (scope) {
            scope.$apply();
          }
        }
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [isPatternFill, boardModel, showBoard]);

  useEffect(() => {
    if (board.type === "table" && presentationMode && elementRef.current) {
      const calculateTableHeight = () => {
        try {
          const containerElement = elementRef.current;
          if (!containerElement) return;

          const containerHeight = containerElement.offsetHeight;
          const titleElement = containerElement.querySelector(".table-board__table-name");
          const titleHeight = titleElement ? titleElement.offsetHeight : 0;
          const calculatedHeight = containerHeight - titleHeight - 40;
          const finalHeight = calculatedHeight > 300 ? calculatedHeight : 300;

          setCalculatedTableHeight(finalHeight);

          setTimeout(() => {
            const resizeEvent = new Event("resize", { bubbles: true });
            window.dispatchEvent(resizeEvent);
          }, 100);
        } catch (error) {
          console.error("Failed to calculate table height:", error);
          setCalculatedTableHeight(600);
        }
      };

      const timeout = setTimeout(calculateTableHeight, 200);

      const handleResize = () => {
        setTimeout(calculateTableHeight, 200);
      };

      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(timeout);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [board.type, presentationMode, showBoard]);

  const getHighChartsFontConfig = useCallback(() => {
    return {
      plotOptions: {
        series: {
          animation: false,
        },
      },
    };
  }, [fontZoomRatio, presentationMode]);

  const handleMetricIndicatorRef = useCallback(ref => {
    metricIndicatorRef.current = ref;
  }, []);

  useEffect(() => {
    if (!board) {
      return;
    }

    setHasNoData(false);

    if (!board.type) {
      board.type = "visualization";
    }

    if (board.type === "table") {
      setTableBoardLoaded(false);
    }

    if (board.type === "text" && board.id && BoardOrganizer) {
      try {
        const existingBoard = BoardOrganizer._findBoard ? BoardOrganizer._findBoard(board.id) : null;
        if (existingBoard && (board.title !== existingBoard.title || board.text !== existingBoard.text)) {
          BoardOrganizer.updateTextBoard(board.id, board.title || "", board.text || "");
        }
      } catch (error) {
        console.warn("TextBoard: Error during text board initialization:", error);
      }
    }

    checkDrilldownButtonAccess();
  }, [board]);

  useEffect(() => {
    if (!boardModel) {
      // CRITICAL FIX: For text boards, show the board area even when boardModel is null
      if (board && board.type === "text") {
        setShowBoard(true);
        setError(null);
      } else {
        setShowBoard(false);
        setMetricLoaded(false);
      }
      // CRITICAL: DO NOT reset tableBoardLoaded here!
      // Angular keeps this value once set - this is why tables disappear
      // setTableBoardLoaded(false); // REMOVED - This was causing data to disappear
      return;
    }

    const shouldShowBoard = (boardModel.showBoard && boardModel.showBoard()) || false;
    setShowBoard(shouldShowBoard);
    if (boardModel.boardError && boardModel.boardError()) {
      setBoardError(boardModel);
    } else {
      setError(null);
    }

    switch (boardModel.type && boardModel.type()) {
      case "metric":
      case "trigger":
        setupMetricBoard(boardModel);
        break;
      case "visualization":
        setupVisualizationBoard(boardModel);
        break;
      case "table":
        setupTableBoard(boardModel);
        break;
    }
  }, [boardModel, board.type]);

  useEffect(() => {
    if (!boardModel || board.type !== "table") {
      previousDataSignature.current = null;
      return;
    }

    const checkDataChanges = () => {
      try {
        const recordset = boardModel.recordset && boardModel.recordset();
        if (!recordset) return;

        const records = (recordset.records && recordset.records()) || [];
        const filterConfig = recordset.filterConfig && recordset.filterConfig();
        const sortField = (filterConfig && filterConfig.sortField && filterConfig.sortField()) || "";

        let filterFieldNames = "";
        try {
          const interpretation = boardModel.interpretation && boardModel.interpretation();
          const interpFilterConfig = interpretation && interpretation.filterConfig && interpretation.filterConfig();
          if (interpFilterConfig && interpFilterConfig.fieldFilterSets) {
            filterFieldNames = interpFilterConfig.fieldFilterSets.map(f => f.fieldName || "").join(",");
          }
        } catch (e) {
          // Ignore errors in filter config access
        }

        const currentSignature = `${records.length}-${sortField}-${filterFieldNames}`;
        if (previousDataSignature.current !== currentSignature) {
          previousDataSignature.current = currentSignature;
          setDataVersion(prev => prev + 1);
        }
      } catch (error) {
        console.warn("Error tracking data changes:", error);
      }
    };

    checkDataChanges();

    const interval = setInterval(checkDataChanges, 500);

    return () => clearInterval(interval);
  }, [boardModel, board.type]);

  useEffect(() => {
    if (globalFilters && !presentationMode) {
      resetScope();
    }
  }, [globalFilters, presentationMode, board.type]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const delayedRedraw = () => {
      if (redrawTimeoutRef.current) {
        clearTimeout(redrawTimeoutRef.current);
      }

      redrawTimeoutRef.current = setTimeout(() => {
        if (["visualization", "metric", "trigger"].includes(board.type)) {
          const chartRedrawEvent = new CustomEvent("chartRedraw", {
            bubbles: true,
            detail: { boardId: board.id, boardType: board.type },
          });
          element.dispatchEvent(chartRedrawEvent);

          const resizeEvent = new Event("resize", { bubbles: true });
          window.dispatchEvent(resizeEvent);

          if (metricIndicatorRef.current && metricIndicatorRef.current.reflow) {
            metricIndicatorRef.current.reflow();
          }
        }
        redrawTimeoutRef.current = null;
      }, 300);
    };

    const resizeObserver = new ResizeObserver(() => delayedRedraw());
    const storyboardContent = element.querySelector(".storyboard-content") || element;
    if (storyboardContent) {
      resizeObserver.observe(storyboardContent);
    }

    return () => {
      resizeObserver.disconnect();
      if (redrawTimeoutRef.current) {
        clearTimeout(redrawTimeoutRef.current);
      }
    };
  }, [board.type, board.id]);

  const handleMouseEnterDelete = useCallback(event => {
    const storyboardBoard = event.target.closest(".storyboard-board");
    if (storyboardBoard) {
      storyboardBoard.classList.add("is-active-delete");
    }
  }, []);

  const handleMouseLeaveDelete = useCallback(event => {
    const storyboardBoard = event.target.closest(".storyboard-board");
    if (storyboardBoard) {
      storyboardBoard.classList.remove("is-active-delete");
    }
  }, []);

  const handleMouseEnterDrilldown = useCallback(() => {
    if (elementRef.current) {
      const storyboardBoard = elementRef.current.closest(".storyboard-board");
      if (storyboardBoard) {
        storyboardBoard.classList.add("is-active-drilldown");
      }
    }
  }, []);

  const handleMouseLeaveDrilldown = useCallback(() => {
    if (elementRef.current) {
      const storyboardBoard = elementRef.current.closest(".storyboard-board");
      if (storyboardBoard) {
        storyboardBoard.classList.remove("is-active-drilldown");
      }
    }
  }, []);

  const removeBoard = useCallback(() => {
    if (onRemoveBoard) {
      onRemoveBoard({ board });
    }
  }, [board, onRemoveBoard]);

  const isMetricOrTrigger = () => board.type === "metric" || board.type === "trigger";

  const getAccessCheckerIdentifier = useCallback(
    targetBoard => {
      const boardToCheck = targetBoard || board;
      const boardType = boardToCheck.type && boardToCheck.type ? boardToCheck.type() : boardToCheck.type;

      if (boardType === "visualization" || boardType === "table") {
        return boardToCheck.interpretation ? boardToCheck.interpretation().id : boardToCheck.interpretation_id;
      }
      return boardToCheck.metricId ? boardToCheck.metricId() : boardToCheck.metricId;
    },
    [board]
  );

  const getDrilldownComponentDetail = useCallback(
    async boardModel => {
      const targetBoard = boardModel || board;

      if (!targetBoard || !AccessChecker) {
        return false;
      }

      const identifier = getAccessCheckerIdentifier(targetBoard);
      const boardType = targetBoard.type && targetBoard.type ? targetBoard.type() : targetBoard.type;
      const entityType = ACCESS_CHECKER_ENTITIES_MAP[boardType];

      const result = await AccessChecker.can(AccessChecker.actions.drilldown, entityType, identifier);
      return result;
    },
    [board, getAccessCheckerIdentifier]
  );

  const retrieveDrilldown = useCallback(
    async boardModel => {
      return await getDrilldownComponentDetail(boardModel);
    },
    [getDrilldownComponentDetail]
  );

  const checkDrilldownButtonAccess = useCallback(async () => {
    if (board && board.type === "text") {
      setIsDrilldown(false);
      return;
    }

    if (editMode) {
      setIsDrilldown(false);
      return;
    }

    if (AccessChecker && board) {
      try {
        const hasAccess = await getDrilldownComponentDetail(boardModel || board);
        setIsDrilldown(hasAccess);
      } catch (error) {
        console.warn("AccessChecker failed, falling back to basic check:", error);
        setIsDrilldown(!editMode && board && board.type !== "text");
      }
    } else {
      setIsDrilldown(!editMode && board && board.type !== "text");
    }
  }, [board, boardModel, editMode, getDrilldownComponentDetail]);

  useEffect(() => {
    if (board) {
      checkDrilldownButtonAccess();
    }
  }, [board, editMode, checkDrilldownButtonAccess]);

  const handleDrilldownClick = useCallback(() => {
    if (AppConfig && AppConfig.features && AppConfig.features.carryStoryboardFilters && hasNoData) {
      if (onDrilldownNoDataConfirmation) {
        onDrilldownNoDataConfirmation({
          confirm: () => handleDrilldown(false),
        });
      }
    } else {
      handleDrilldown(true);
    }
  }, [hasNoData, onDrilldownNoDataConfirmation]);

  const handleDrilldown = useCallback(
    carryFilters => {
      if (storyboardModel && storyboardModel.handleDrilldown) {
        storyboardModel.handleDrilldown(board, carryFilters);
      }
    },
    [board, storyboardModel]
  );

  const setupVisualizationBoard = useCallback(visualizationBoard => {
    if (
      visualizationBoard.boardError &&
      visualizationBoard.boardError() &&
      visualizationBoard.boardError().originalError() === ERROR_CODES.VISUALIZATION_NO_DATA
    ) {
      setHasNoData(true);
    } else {
      setHasNoData(false);
    }
  }, []);

  const setupTableBoard = useCallback(
    tableBoard => {
      if (tableBoard.isValid && tableBoard.isValid()) {
        const recordset = tableBoard.recordset && tableBoard.recordset();
        if (recordset && recordset.recordCount && recordset.recordCount() === 0) {
          setHasNoData(true);
        } else {
          setHasNoData(false);
        }
        setTableBoardLoaded(true);
      } else if (tableBoard.boardError && tableBoard.boardError()) {
        setBoardError(tableBoard);
        setTableBoardLoaded(false);
      } else {
        setTableBoardLoaded(false);
      }
    },
    [setBoardError]
  );

  const getBoardModelId = useCallback((metricBoardModel, board) => {
    if (!metricBoardModel) {
      return `${board.id}-unknown`;
    }

    if (metricBoardModel.id) {
      return metricBoardModel.id();
    }

    if (metricBoardModel._id) {
      return metricBoardModel._id;
    }

    const type = metricBoardModel.type ? metricBoardModel.type() : "unknown";
    return `${board.id}-${type}`;
  }, []);

  const setupMetricBoard = useCallback(
    metricBoardModel => {
      const boardModelId = getBoardModelId(metricBoardModel, board);

      const newMetricBoard = {};

      if (metricBoardModel && metricBoardModel.isValid()) {
        const metric = metricBoardModel.metric && metricBoardModel.metric();
        newMetricBoard.metric = metric;
        let conditionGroupId = metricBoardModel.conditionGroupId && metricBoardModel.conditionGroupId();
        newMetricBoard.conditionGroupId = conditionGroupId;
        newMetricBoard.name = metricBoardModel.metricName && metricBoardModel.metricName();
        newMetricBoard.type = metricBoardModel.metricType && metricBoardModel.metricType();
        newMetricBoard.showSparkline = metricBoardModel.showSparkline && metricBoardModel.showSparkline();

        const table = metricBoardModel.table && metricBoardModel.table();
        if (table) {
          const fieldName = metricBoardModel.fieldName && metricBoardModel.fieldName();
          const timeFieldName = metricBoardModel.timeFieldName && metricBoardModel.timeFieldName();

          if (fieldName && table) {
            const tableModel =
              table ||
              (storyboardModel &&
                storyboardModel.getTable &&
                board.controlTestId &&
                storyboardModel.getTable(board.controlTestId));
            newMetricBoard.field = tableModel && tableModel.fieldByName ? tableModel.fieldByName(fieldName) : undefined;
          }

          if (timeFieldName && table) {
            const tableModel =
              table ||
              (storyboardModel &&
                storyboardModel.getTable &&
                board.controlTestId &&
                storyboardModel.getTable(board.controlTestId));
            newMetricBoard.timeField =
              tableModel && tableModel.fieldByName ? tableModel.fieldByName(timeFieldName) : undefined;
          }
        }

        const metricSparkline = metricBoardModel.metricSparkline && metricBoardModel.metricSparkline();
        newMetricBoard.sparklineData = metricSparkline;

        if (
          metricSparkline &&
          metricSparkline.values &&
          metricSparkline.values() &&
          metricSparkline.values().length === 0
        ) {
          setHasNoData(true);
        } else {
          setHasNoData(false);
        }

        setMetricBoard(newMetricBoard);
        setMetricLoaded(true);
        setProcessedBoardModelId(boardModelId);
      }
    },
    [getBoardModelId, board, presentationMode, processedBoardModelId, metricLoaded]
  );

  const setBoardError = useCallback(boardModel => {
    const boardError = boardModel.boardError();
    const boardErrorLocalizeKey = boardError.localizeKey();

    const fallbackMessages = {
      "_Storyboards.Visualization.NotAvailable_": "The visualization has been deleted.",
      "_Storyboards.Interpretation.NotFound_": "The interpretation has been deleted.",
      "_Storyboards.Visualization.Error_": "Error loading visualization.",
      "_Storyboards.Interpretation.Error_": "Error loading interpretation.",
      "_Storyboards.Metric.NotAvailable_": "The metric has been deleted.",
      "_Storyboards.Metric.Error_": "Error loading metric.",
    };

    let localizedMessage = boardErrorLocalizeKey;

    if (Localize && Localize.getLocalizedString) {
      try {
        const localized = Localize.getLocalizedString(boardErrorLocalizeKey);
        if (localized && localized !== boardErrorLocalizeKey) {
          localizedMessage = localized;
        } else {
          localizedMessage = fallbackMessages[boardErrorLocalizeKey] || boardErrorLocalizeKey;
        }
      } catch (e) {
        console.warn("Localization failed for key:", boardErrorLocalizeKey, e);
        localizedMessage = fallbackMessages[boardErrorLocalizeKey] || boardErrorLocalizeKey;
      }
    } else {
      localizedMessage = fallbackMessages[boardErrorLocalizeKey] || boardErrorLocalizeKey;
    }

    setError({
      message: localizedMessage || "Board error",
      type: (boardError.originalError && boardError.originalError()) || "error",
    });
  }, []);

  const resetScope = useCallback(() => {
    setMetricBoard({});
    setMetricLoaded(false);
    setProcessedBoardModelId(null);

    if (board.type === "table") {
      setTableBoardLoaded(false);
    }
  }, [presentationMode, board.type, board.id]);

  const handleTextBoardChange = useCallback(
    fields => {
      if (!isValidTextBoardUpdate(board, fields)) return;

      try {
        if (BoardOrganizer) {
          BoardOrganizer.updateTextBoard(board.id, fields.title || "", fields.text || "");
        }
        updateBoardFields(board, fields);

        if (boardModel && typeof boardModel.updateTextBoard === "function") {
          try {
            boardModel.updateTextBoard(fields);
          } catch (error) {
            console.warn("TextBoard: Error updating boardModel (non-critical):", error);
          }
        }
        trackTextBoardChanges();
      } catch (error) {
        if (UnsavedData && typeof UnsavedData.set === "function") {
          UnsavedData.set(true);
        }
      }
    },
    [board, boardModel]
  );

  const isValidTextBoardUpdate = (board, fields) => {
    return board && board.id && fields && (fields.title !== undefined || fields.text !== undefined);
  };

  const updateBoardFields = (board, fields) => {
    const updates = {};
    if (fields.title !== undefined) updates.title = fields.title || "";
    if (fields.text !== undefined) updates.text = fields.text || "";
    Object.assign(board, updates);
  };

  const trackTextBoardChanges = () => {
    try {
      const injector = window.angular.element(document.body).injector();
      const freshUnsavedData = injector.get("UnsavedData");
      if (freshUnsavedData && typeof freshUnsavedData.set === "function") {
        freshUnsavedData.set(true);
        return;
      }
    } catch (error) {}

    if (UnsavedData && typeof UnsavedData.set === "function") {
      UnsavedData.set(true);
    }
  };

  const prepareTableData = useMemo(() => {
    if (!boardModel || board.type !== "table") return null;

    const recordset = boardModel.recordset && boardModel.recordset();
    const table = boardModel.table && boardModel.table();
    const interpretation = boardModel.interpretation && boardModel.interpretation();

    if (!recordset || !table || !interpretation) return null;

    const rawData = (recordset.records && recordset.records()) || [];

    const rawFields = (table.fields && table.fields()) || [];
    const filterFieldNames =
      (DataFilter && DataFilter.getFilterFieldNames && DataFilter.getFilterFieldNames(interpretation.filterConfig)) ||
      [];
    const sortFieldName =
      (recordset.filterConfig &&
        recordset.filterConfig() &&
        recordset.filterConfig().sortField &&
        recordset.filterConfig().sortField()) ||
      "";
    const sortOrder =
      (recordset.filterConfig &&
        recordset.filterConfig() &&
        recordset.filterConfig().sortOrder &&
        recordset.filterConfig().sortOrder()) ||
      "";

    const fields = rawFields.map(fieldObj => {
      const fieldData = fieldObj._data || fieldObj;

      const field = new Field()
        .name(fieldData.name)
        .displayName(fieldData.displayName)
        .type(fieldData.type)
        .isPrimaryKey(fieldData.isPrimaryKey || false)
        .tableId(fieldData.tableId);
      return field;
    });

    // Initialize conditional formatting for table boards
    const fieldFormatIdentifier =
      (interpretation.id && interpretation.id.toString()) || (board.id && board.id.toString()) || "table-board";

    if (interpretation.tableConfig && interpretation.tableConfig.formatting) {
      // Create fields map for conditional formatting
      const fieldsMap = {};
      fields.forEach(field => {
        fieldsMap[field.name()] = {
          type: field.type(),
        };
      });

      // Update conditional formatting conditions
      ConditionalFormatting.updateFormattingConditions(
        fieldsMap,
        interpretation.tableConfig.formatting,
        fieldFormatIdentifier
      );
    }

    const columnConfigs = ((interpretation.tableConfig && interpretation.tableConfig.columns) || []).map(colObj => {
      const colData = colObj._data || colObj;
      const fieldName = colData.name || colData.fieldName;

      const width =
        typeof colData.width === "number"
          ? colData.width
          : typeof colData.columnWidth === "number"
          ? colData.columnWidth
          : null;

      const columnConfig = new ColumnConfig()
        .name(fieldName)
        .visible(colData.visible !== false)
        .wordWrap(!!colData.wordWrap)
        .width(width);

      return columnConfig;
    });

    const transformedData = rawData.map((row, rowIndex) => {
      const transformedRow = { ...row };
      let positionValue = null;
      const possiblePositionColumns = Object.keys(row).filter(key => {
        const value = row[key];
        if (typeof value === "string") {
          return /^[A-Za-z]+\s*-\s*\d+$/.test(value.trim());
        }
        return false;
      });

      if (possiblePositionColumns.length > 0) {
        const positionColumn = possiblePositionColumns[0];
        const match = row[positionColumn].toString().match(/(\d+)/);
        if (match) {
          positionValue = parseInt(match[1], 10);
        }
      }

      if (positionValue !== null) {
        transformedRow["metadata.position"] = positionValue;
      } else if (row["metadata.position"] !== undefined && row["metadata.position"] !== null) {
        const parsedPosition =
          typeof row["metadata.position"] === "number"
            ? Math.round(row["metadata.position"])
            : parseInt(row["metadata.position"], 10);
        transformedRow["metadata.position"] = isNaN(parsedPosition) ? rowIndex + 1 : parsedPosition;
      } else {
        transformedRow["metadata.position"] = rowIndex + 1;
      }

      return transformedRow;
    });

    const getFixedColumnsLeft = () => {
      try {
        if (AppConfig.features.fixedColumnLeftInViz) {
          return tableCapability.canRemediateRecords() ? 2 : 1;
        }
        return 0;
      } catch (error) {
        return 0;
      }
    };

    const createTableConfig = data => {
      return {
        data: data,
        fields: fields,
        fieldFormatIdentifier: fieldFormatIdentifier,
        fontZoomRatio: fontZoomRatio || 1,
        isPresentationMode: presentationMode || false,
        isRowHighlightingEnabled: true,
        isShrinkWrapEnabled: !presentationMode,
        outsideClickDeselects: presentationMode || false,
        filterFieldNames: filterFieldNames,
        sortFieldName: sortFieldName,
        sortOrder: sortOrder,
        columnConfigs: columnConfigs,
        fixedColumnsLeft: getFixedColumnsLeft(),
        isPositionEnabled: AppConfig.features.remediationColumns || true,
        positionKey: "metadata.position",
        rowIdKey: "metadata.exception_id",
        isCommentsEnabled: AppConfig.features.remediationColumns && tableCapability.canRemediateRecords(),
        detectScrollToBottomByHeight: presentationMode,
        onScrollToBottom: () => {
          onLoadMoreData({ board });
        },
      };
    };

    if (typeof rawData === "string" || !Array.isArray(rawData)) {
      return createTableConfig([]);
    }

    return createTableConfig(transformedData);
  }, [boardModel, board.type, board.id, fontZoomRatio, presentationMode, onLoadMoreData, dataVersion]);

  const prepareTextBoardProps = useCallback(() => {
    if (board.type !== "text") return null;

    const safeTitle = (() => {
      if (typeof board.title === "string" && board.title.trim() !== "") {
        return board.title;
      }
      if (board.title === undefined || board.title === null) {
        return "";
      }
      return String(board.title || "");
    })();

    const safeText = (() => {
      if (typeof board.text === "string" && board.text.trim() !== "") {
        return board.text;
      }
      if (board.text === undefined || board.text === null) {
        return "";
      }
      return String(board.text || "");
    })();

    let palette = [];
    try {
      if (ColorPalette && typeof ColorPalette === "function") {
        const colorPal = new ColorPalette();
        palette = colorPal
          .darkColors()
          .concat(colorPal.baseColors())
          .concat(colorPal.lightColors())
          .concat(colorPal.lighterColors())
          .concat(colorPal.greyColors());
      } else {
        palette = [
          "#1f2937",
          "#374151",
          "#4b5563",
          "#ef4444",
          "#f97316",
          "#f59e0b",
          "#eab308",
          "#84cc16",
          "#22c55e",
          "#06b6d4",
          "#3b82f6",
          "#6366f1",
          "#8b5cf6",
          "#a855f7",
          "#d946ef",
          "#fecaca",
          "#fed7aa",
          "#fde68a",
          "#fef3c7",
          "#d9f99d",
          "#bbf7d0",
          "#a7f3d0",
          "#bfdbfe",
          "#c7d2fe",
          "#ddd6fe",
          "#e9d5ff",
          "#f3e8ff",
          "#fef2f2",
          "#fff7ed",
          "#fffbeb",
          "#fefce8",
          "#f7fee7",
          "#f0fdf4",
          "#f0fdfa",
          "#eff6ff",
          "#eef2ff",
          "#f5f3ff",
          "#faf5ff",
          "#fdf4ff",
          "#f9fafb",
          "#f3f4f6",
          "#e5e7eb",
          "#d1d5db",
          "#9ca3af",
          "#6b7280",
          "#4b5563",
          "#374151",
          "#1f2937",
          "#111827",
        ];
      }
    } catch (error) {
      console.warn("ColorPalette not available, using fallback colors:", error);
      palette = [
        "#1f2937",
        "#374151",
        "#4b5563",
        "#ef4444",
        "#f97316",
        "#f59e0b",
        "#eab308",
        "#84cc16",
        "#22c55e",
        "#06b6d4",
        "#3b82f6",
        "#6366f1",
        "#8b5cf6",
        "#a855f7",
        "#d946ef",
        "#fecaca",
        "#fed7aa",
        "#fde68a",
        "#fef3c7",
        "#d9f99d",
        "#bbf7d0",
        "#a7f3d0",
        "#bfdbfe",
        "#c7d2fe",
        "#ddd6fe",
        "#e9d5ff",
        "#f3e8ff",
        "#fef2f2",
        "#fff7ed",
        "#fffbeb",
        "#fefce8",
        "#f7fee7",
        "#f0fdf4",
        "#f0fdfa",
        "#eff6ff",
        "#eef2ff",
        "#f5f3ff",
        "#faf5ff",
        "#fdf4ff",
        "#f9fafb",
        "#f3f4f6",
        "#e5e7eb",
        "#d1d5db",
        "#9ca3af",
        "#6b7280",
        "#4b5563",
        "#374151",
        "#1f2937",
        "#111827",
      ];
    }

    return {
      boardId: (board.id && board.id.toString()) || "text-board",
      title: safeTitle,
      text: safeText,
      editMode: editMode || false,
      palette: palette,
      onChange: handleTextBoardChange,
    };
  }, [boardModel, board.type, board.id, board.title, board.text, editMode, handleTextBoardChange, presentationMode]);

  const renderTextBoard = useCallback(() => {
    if (board.type !== "text" || AppConfig.features.enableStoryboardReactTextBoards === false) {
      return null;
    }

    const textBoardProps = prepareTextBoardProps();
    return textBoardProps ? (
      <div className="storyboard-content__text-template">
        <TextBoard {...textBoardProps} />
      </div>
    ) : (
      <div className="text-board__error">Unable to load text board data</div>
    );
  }, [board.type, prepareTextBoardProps]);

  const renderTableBoard = useCallback(() => {
    // Only render if it's a table type - allow rendering even without boardModel to show loader
    if (board.type !== "table" || error) {
      return null;
    }

    return (
      <div className="storyboard-content__table-template">
        {/* Show loader while data is loading - show if no boardModel OR data not ready */}
        {(!boardModel || !showBoard || !tableBoardLoaded) && (
          <div className="storyboard-board__loading">
            <TileLoading />
          </div>
        )}

        {/* Show table when data is ready - matching Angular pattern */}
        {boardModel &&
          showBoard &&
          tableBoardLoaded &&
          prepareTableData &&
          (() => {
            const getTableTitle = () => {
              try {
                const interpretation = boardModel.interpretation && boardModel.interpretation();
                if (interpretation && interpretation.visualizationInfo && interpretation.visualizationInfo.title) {
                  return interpretation.visualizationInfo.title;
                }

                if (board.title) {
                  return board.title;
                }

                return "Table";
              } catch (error) {
                return board.title || "Table";
              }
            };

            return (
              <div className="storyboard-board__table-board">
                <div className="table-board__table-name">{getTableTitle()}</div>
                <div
                  ref={tableContainerRef}
                  className="table-board__data-grid"
                  style={calculatedTableHeight && presentationMode ? { height: `${calculatedTableHeight}px` } : {}}
                >
                  <DataGrid {...prepareTableData} />
                </div>
              </div>
            );
          })()}
      </div>
    );
  }, [
    board.type,
    boardModel,
    showBoard,
    error,
    tableBoardLoaded,
    prepareTableData,
    board.title,
    calculatedTableHeight,
    presentationMode,
  ]);

  const renderMetricBoard = useCallback(() => {
    if (!isMetricOrTrigger() || !boardModel) {
      return null;
    }

    return (
      <div className="storyboard-content__metric-template">
        {!showBoard && !error && showLoadingSpinner && !metricLoaded && (
          <div className="storyboard-board__loading">
            <TileLoading />
          </div>
        )}

        {showBoard && !error && metricLoaded && (
          <div className="sriracha storyboard-board__metric-board" data-component="react-storyboard-board">
            {metricBoard.metric && (
              <MetricHeader
                metric={metricBoard.metric.toJson ? metricBoard.metric.toJson() : metricBoard.metric}
                thresholdId={metricBoard.conditionGroupId}
                metricName={metricBoard.name}
                metricType={metricBoard.type}
              />
            )}

            <div className="metric-board__metric-indicator">
              <div className="metric-indicator__container" style={{ height: "100%" }}>
                <MetricIndicator
                  ref={handleMetricIndicatorRef}
                  className="sriracha"
                  style={{
                    height: "100%",
                    display: "block",
                    width: "100%",
                  }}
                  showOnlyThreshold={metricBoard.conditionGroupId || null}
                  metric={(() => {
                    const metric =
                      metricBoard.metric && metricBoard.metric.toJson
                        ? metricBoard.metric.toJson()
                        : metricBoard.metric;

                    if (metric && metric.thresholds && Array.isArray(metric.thresholds)) {
                      metric.thresholds = metric.thresholds.map((threshold, index) => {
                        const numericValues = threshold.values ? threshold.values.map(v => parseFloat(v)) : [];
                        const processedThreshold = {
                          ...threshold,
                          values: numericValues,
                          operator: threshold.operator || ">",
                          indicator_color: threshold.indicator_color || "#CD3C44",
                          id: threshold.id || threshold.condition_group_id || `threshold_${index}`,
                          condition_group_id: threshold.condition_group_id || threshold.id,
                          trigger:
                            threshold.trigger && threshold.trigger.enabled !== false
                              ? threshold.trigger
                              : { enabled: true },
                        };
                        return processedThreshold;
                      });

                      metric.view_thresholds = true;
                      metric.show_sparkline = true;
                    }

                    return metric;
                  })()}
                  metricSparkline={
                    metricBoard.sparklineData && metricBoard.sparklineData.toJson
                      ? metricBoard.sparklineData.toJson()
                      : metricBoard.sparklineData
                  }
                  showInsufficientSparklineDataMessage={editMode}
                  showSparkline={metricBoard.showSparkline !== false}
                  presentationMode={presentationMode}
                  fontZoomRatio={fontZoomRatio}
                  locale={storyboardModel && storyboardModel.locale}
                  timezoneOffsetSeconds={GlobalValueFormatter.getTimezoneOffset().utcOffsetSeconds()}
                  hcFontConfig={getHighChartsFontConfig()}
                  compact={false}
                  thumbnail={false}
                  fullSize={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    isMetricOrTrigger,
    boardModel,
    showBoard,
    error,
    showLoadingSpinner,
    metricLoaded,
    metricBoard,
    editMode,
    presentationMode,
    fontZoomRatio,
    storyboardModel,
    getHighChartsFontConfig,
    handleMetricIndicatorRef,
  ]);

  return (
    <div
      ref={elementRef}
      id={`storyboard-board-${board.id}`}
      className={`storyboard-content board-type__${board.type || "visualization"} ${
        presentationMode ? "presentation-mode-content" : ""
      }`}
      data-board-id={board.id}
      data-board-type={board.type}
    >
      {error && (
        <div className="storyboard-board__error">
          <BoardNotification message={error.message} type={error.type} />
        </div>
      )}

      {/* Loader for visualizations - shown before chart loads (always show for better UX) */}
      {!showBoard && !error && (board.type === "visualization" || !board.type) && (
        <div className="storyboard-board__loading">
          <TileLoading />
        </div>
      )}

      {/* Text boards */}
      {renderTextBoard()}

      {/* Visualizations - show when ready */}
      {(!board.type || board.type === "visualization") && boardModel && showBoard && !error && (
        <VisualizationBoardContainer
          boardModel={boardModel}
          editMode={editMode}
          fontZoomRatio={fontZoomRatio}
          presentationMode={presentationMode}
          dataModel={boardModel}
          isPatternFill={isPatternFill}
          onMouseEnterDrilldown={handleMouseEnterDrilldown}
          onMouseLeaveDrilldown={handleMouseLeaveDrilldown}
        />
      )}

      {/* Tables - includes its own loader inside */}
      {renderTableBoard()}

      {/* Metrics - includes its own loader inside */}
      {renderMetricBoard()}

      {isDrilldown && (
        <StoryBoardDrilldownButton
          onDrilldownClick={handleDrilldownClick}
          onMouseEnterDrilldown={handleMouseEnterDrilldown}
          onMouseLeaveDrilldown={handleMouseLeaveDrilldown}
          presentationMode={presentationMode}
          isDrilldown={isDrilldown}
        />
      )}

      {editMode && !presentationMode && (
        <button
          className="storyboard-icon storyboard-remove-board"
          onClick={removeBoard}
          onMouseEnter={handleMouseEnterDelete}
          onMouseLeave={handleMouseLeaveDelete}
          aria-label="Remove board"
        >
          <i className="acl-i-trashbin"></i>
        </button>
      )}
    </div>
  );
};

StoryboardBoard.propTypes = {
  board: PropTypes.object.isRequired,
  editMode: PropTypes.bool,
  fontZoomRatio: PropTypes.number,
  globalFilters: PropTypes.object,
  storyboardModel: PropTypes.object,
  boardModel: PropTypes.object,
  onRemoveBoard: PropTypes.func,
  onLoadMoreData: PropTypes.func,
  presentationMode: PropTypes.bool,
  onDrilldownNoDataConfirmation: PropTypes.func,
};

StoryboardBoard.defaultProps = {
  editMode: false,
  fontZoomRatio: 1,
  presentationMode: false,
  globalFilters: {},
  onRemoveBoard: () => {},
  onLoadMoreData: () => {},
  onDrilldownNoDataConfirmation: () => {},
};

export default StoryboardBoard;
